import {ProgressAria} from '_components/ProgressAria';
import LFCSingleValue from '_components/surfaces/LFCSingleValue';
import {fmtThreeCommas} from '_logics/LFCFormatUtil';
import {useParseWithSettings} from '_logics/LFCParseUtil';
import {getLFCData} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import {isEmpty} from 'lodash';
import {useSnackbar} from 'notistack';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import {green, red} from '@mui/material/colors';

import {calculatePercent} from '../logics';
import {SearchConditions} from '../types';
import {MatchingChart} from './MatchingChart';
import {MatchingRateChart} from './MatchingRateChart';
import {MatchingRatePie} from './MatchingRatePie';

interface ChartPanelProps {
  searchConditions: SearchConditions;
  selectedMap: string;
  xTimeliles: string[];
}

export const ChartPanel = (props: ChartPanelProps) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const {parsePercent} = useParseWithSettings();
  const [startProcess, setStartProcess] = useState<boolean>(false);
  const cancelSource = useRef<CancelTokenSource | null>(null);
  const [dsOkRateTrends, setDsOkRateTrends] = useState<Record<string, any>[]>([]);
  const [dsInspectionSummaryData, setDsInspectionSummaryData] = useState<Record<
    string,
    any
  > | null>(null);

  const [zoomChartOpen, setZoomChartOpen] = useState<string>('');

  /**
   * 初期処理
   */
  useEffect(() => {
    if (props.searchConditions === null) return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      //マッチング率
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 30111,
        parameters: {
          search_type: props.searchConditions.searchType,
          select_date_from: props.searchConditions.selectDateFrom,
          select_date_to: props.searchConditions.selectDateTo,
          work: !isEmpty(props.searchConditions.work)
            ? '{' + props.searchConditions.work.join(',') + '}'
            : null,
          deviceid: !isEmpty(props.searchConditions.deviceid)
            ? '{' + props.searchConditions.deviceid.join(',') + '}'
            : null,
          judge: props.searchConditions.judge === 9 ? null : props.searchConditions.judge,
          map: props.selectedMap
        },
        name: 'マッチング率',
        cancelToken: cancelSource.current.token,
        t
      }).then(data => {
        setDsOkRateTrends(data);
      }),
      //検査数等集計データ
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 30112,
        parameters: {
          search_type: props.searchConditions.searchType,
          select_date_from: props.searchConditions.selectDateFrom,
          select_date_to: props.searchConditions.selectDateTo,
          work: !isEmpty(props.searchConditions.work)
            ? '{' + props.searchConditions.work.join(',') + '}'
            : null,
          deviceid: !isEmpty(props.searchConditions.deviceid)
            ? '{' + props.searchConditions.deviceid.join(',') + '}'
            : null,
          judge: props.searchConditions.judge === 9 ? null : props.searchConditions.judge,
          map: props.selectedMap
        },
        name: '検査数等集計データ',
        cancelToken: cancelSource.current.token,
        t
      }).then(data => {
        setDsInspectionSummaryData(data[0]);
      })
    ]).finally(() => {
      setStartProcess(false);
    });

    return () => {
      // アンマウント処理
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, [enqueueSnackbar, props.searchConditions, props.selectedMap, t]);

  return (
    <ProgressAria startProcess={startProcess}>
      <Box mb={1}>
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary
            id={'ChartPanel_' + props.selectedMap}
            expandIcon={<ExpandMoreIcon />}
            aria-controls="ChartPanel_content"
          >
            <Typography component="span" sx={{fontWeight: 'bold'}}>
              {props.selectedMap}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{padding: 1}}>
            <Grid container spacing={0}>
              <Grid item xs={12} sm={8} lg={9}>
                <MatchingChart
                  title={`${t('ＯＫ点群率')}`}
                  source={dsOkRateTrends}
                  xlist={props.xTimeliles}
                  searchType={props.searchConditions.searchType}
                  exportFilename={'SurfaceCompare4Step_MatchingChart_' + props.selectedMap}
                  height={'300px'}
                  zoomChartId={'zoom-MatchingChart-' + props.selectedMap}
                  zoomedChartId={zoomChartOpen}
                  zoomCharts={() => setZoomChartOpen('zoom-MatchingChart-' + props.selectedMap)}
                  zoomChartsClose={() => setZoomChartOpen('')}
                  onClick={() => {}}
                />
              </Grid>
              <Grid item xs={12} sm={4} lg={3} sx={{display: 'flex', alignItems: 'center'}}>
                <Stack spacing={2} sx={{width: '100%'}}>
                  <LFCSingleValue
                    title={t('検査数')}
                    val={fmtThreeCommas(dsInspectionSummaryData?.total_count)}
                    titleVariant="caption"
                    valVariant="body1"
                    key={t('検査数')}
                    fontColor={green[500]}
                  />
                  <Box style={{marginTop: '2px'}}>
                    <Grid container>
                      <Grid item xs={6}>
                        <LFCSingleValue
                          title={t('良品')}
                          val={fmtThreeCommas(dsInspectionSummaryData?.ok_count)}
                          titleVariant="caption"
                          valVariant="body1"
                          key={t('良品')}
                          fontColor={green[500]}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <LFCSingleValue
                          title={t('不良品')}
                          val={fmtThreeCommas(dsInspectionSummaryData?.ng_count)}
                          titleVariant="caption"
                          valVariant="body1"
                          key={t('不良品')}
                          fontColor={red[500]}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <LFCSingleValue
                    title={t('不良率')}
                    val={parsePercent(
                      calculatePercent(
                        dsInspectionSummaryData?.ng_count || 0,
                        dsInspectionSummaryData?.total_count || 0
                      )
                    )}
                    titleVariant="caption"
                    valVariant="body1"
                    key={t('不良率')}
                    fontColor={red[500]}
                    unit={'%'}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={8} lg={9}>
                <MatchingRateChart
                  title={`${t('合計点群の比率')}`}
                  source={dsOkRateTrends}
                  xlist={props.xTimeliles}
                  searchType={props.searchConditions.searchType}
                  exportFilename={'SurfaceCompare4Step_MatchingRateChart_' + props.selectedMap}
                  height={'300px'}
                  zoomChartId={'zoom-MatchingRateChart-' + props.selectedMap}
                  zoomedChartId={zoomChartOpen}
                  zoomCharts={() => setZoomChartOpen('zoom-MatchingRateChart-' + props.selectedMap)}
                  zoomChartsClose={() => setZoomChartOpen('')}
                  onClick={() => {}}
                />
              </Grid>
              <Grid item xs={12} sm={4} lg={3}>
                <MatchingRatePie
                  title={`${t('合計点群の比率')}`}
                  source={dsOkRateTrends}
                  exportFilename={'SurfaceCompare4Step_MatchingRatePie_' + props.selectedMap}
                  height={'300px'}
                  zoomChartId={'zoom-MatchingRatePie-' + props.selectedMap}
                  zoomedChartId={zoomChartOpen}
                  zoomCharts={() => setZoomChartOpen('zoom-MatchingRatePie-' + props.selectedMap)}
                  zoomChartsClose={() => setZoomChartOpen('')}
                  onClick={() => {}}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
    </ProgressAria>
  );
};
